import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, Code, FormControl,
  FormHelperText, FormLabel, HStack, IconButton, Image, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Radio, RadioGroup, Table, TableContainer, Tbody, Td, Text, Textarea, Th,
  Thead, Tr, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, CloseIcon } from '@chakra-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import customFieldService from '../services/customFieldService';

const KEY_RE = /^[a-z][a-z0-9_]*$/;

// Mirrors the backend gate: these render into guest-facing emails and PDFs.
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

// Stored image URLs come back relative (/media/…) in local dev and live on
// the API host, not the admin host.
const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').origin;
  } catch {
    return '';
  }
})();
const absUrl = (u) => (u && u.startsWith('/') ? `${API_ORIGIN}${u}` : (u || ''));

// '202-1' → '202_1' — mirrors the backend postfix sanitiser so the
// hint chips show the exact placeholder that will work.
const sanitize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9_]/g, '_');

const EMPTY_FORM = {
  key: '', scope: 'hotel', fieldType: 'text', value: '', roomValues: {},
  imageFile: null, imagePreview: '', imageUrl: '', clearImage: false,
  roomImageFiles: {}, roomImagePreviews: {}, roomImages: {}, clearRoomImages: {},
};

const CustomFields = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ fields: [], rooms: [], reservedKeys: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const modal = useDisclosure();
  const deleteModal = useDisclosure();
  const toast = useToast();
  const hotelFileRef = useRef(null);
  const roomFileRef = useRef(null);
  const roomPickTarget = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      setData(await customFieldService.get());
    } catch {
      toast({ title: t('customFields.toasts.loadFailed'), status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    modal.onOpen();
  };

  const openEdit = (field) => {
    setEditingId(field.id);
    setForm({
      ...EMPTY_FORM,
      key: field.key,
      scope: field.scope,
      fieldType: field.fieldType || 'text',
      value: field.value || '',
      roomValues: { ...(field.roomValues || {}) },
      imageUrl: field.imageUrl || '',
      roomImages: { ...(field.roomImages || {}) },
    });
    modal.onOpen();
  };

  const imageError = (file) => {
    if (!IMAGE_TYPES.includes(file.type)) return t('customFields.errors.imageType');
    if (file.size > MAX_IMAGE_BYTES) return t('customFields.errors.imageSize');
    return null;
  };

  const pickHotelImage = (file) => {
    const err = imageError(file);
    if (err) {
      toast({ title: err, status: 'error' });
      return;
    }
    setForm((f) => ({
      ...f, imageFile: file, imagePreview: URL.createObjectURL(file), clearImage: false,
    }));
  };

  const removeHotelImage = () => setForm((f) => ({
    ...f, imageFile: null, imagePreview: '', imageUrl: '', clearImage: true,
  }));

  const pickRoomImage = (roomId, file) => {
    const err = imageError(file);
    if (err) {
      toast({ title: err, status: 'error' });
      return;
    }
    setForm((f) => {
      const clearRoomImages = { ...f.clearRoomImages };
      delete clearRoomImages[roomId];
      return {
        ...f,
        roomImageFiles: { ...f.roomImageFiles, [roomId]: file },
        roomImagePreviews: { ...f.roomImagePreviews, [roomId]: URL.createObjectURL(file) },
        clearRoomImages,
      };
    });
  };

  const removeRoomImage = (roomId) => setForm((f) => {
    const roomImageFiles = { ...f.roomImageFiles };
    const roomImagePreviews = { ...f.roomImagePreviews };
    const roomImages = { ...f.roomImages };
    delete roomImageFiles[roomId];
    delete roomImagePreviews[roomId];
    delete roomImages[roomId];
    return {
      ...f, roomImageFiles, roomImagePreviews, roomImages,
      clearRoomImages: { ...f.clearRoomImages, [roomId]: true },
    };
  });

  const handleSave = async () => {
    const key = form.key.trim().toLowerCase();
    if (!KEY_RE.test(key)) {
      toast({
        title: t('customFields.toasts.invalidKey'),
        description: t('customFields.toasts.invalidKeyHelp'),
        status: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      let res;
      if (form.fieldType === 'image') {
        const fd = new FormData();
        fd.append('key', key);
        fd.append('scope', form.scope);
        fd.append('fieldType', 'image');
        if (form.scope === 'hotel') {
          if (form.imageFile) fd.append('image', form.imageFile);
          else if (form.clearImage && editingId) fd.append('clearImage', 'true');
        } else {
          Object.entries(form.roomImageFiles).forEach(([roomId, file]) => {
            fd.append(`image_room_${roomId}`, file);
          });
          if (editingId) {
            Object.keys(form.clearRoomImages).forEach((roomId) => {
              fd.append(`clear_image_room_${roomId}`, 'true');
            });
          }
        }
        res = editingId
          ? await customFieldService.updateForm(editingId, fd)
          : await customFieldService.createForm(fd);
      } else {
        const payload = { key, scope: form.scope };
        if (form.scope === 'hotel') payload.value = form.value;
        else payload.roomValues = form.roomValues;
        res = editingId
          ? await customFieldService.update(editingId, payload)
          : await customFieldService.create(payload);
      }
      setData(res);
      toast({ title: res.message || t('customFields.toasts.saved'), status: 'success' });
      modal.onClose();
    } catch (e) {
      toast({
        title: t('customFields.toasts.saveFailed'),
        description: e?.response?.data?.detail || e?.error || '',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await customFieldService.remove(deleteTarget.id);
      setData(res);
      toast({ title: res.message || t('customFields.toasts.removed'), status: 'info' });
      deleteModal.onClose();
    } catch {
      toast({ title: t('customFields.toasts.removeFailed'), status: 'error' });
    }
  };

  const roomValueCount = (field) =>
    Object.values(field.roomValues || {}).filter((v) => v && String(v).trim()).length;

  const roomImageCount = (field) =>
    Object.values(field.roomImages || {}).filter(Boolean).length;

  const openRoomPicker = (roomId) => {
    roomPickTarget.current = roomId;
    roomFileRef.current.click();
  };

  return (
    <PageShell
      title={t('customFields.title')}
      subtitle={t('customFields.subtitle')}
      breadcrumbs={[{ label: t('customFields.title') }]}
      loading={loading}
      actions={
        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>
          {t('customFields.buttons.addKey')}
        </Button>
      }
    >
      <VStack align="stretch" spacing={4}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box fontSize="sm">
            <Trans
              i18nKey="customFields.intro"
              values={{ keyTag: '{{key}}', urlTag: '{{key_url}}', roomTag: '{{key_101}}' }}
              components={{ code: <Code /> }}
            />
          </Box>
        </Alert>

        <Card shadow="sm" borderWidth="1px">
          <CardBody p={0}>
            <TableContainer>
              <Table size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>{t('customFields.table.placeholder')}</Th>
                    <Th>{t('customFields.table.scope')}</Th>
                    <Th>{t('customFields.table.value')}</Th>
                    <Th w="90px">{t('common.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.fields.length === 0 && (
                    <Tr>
                      <Td colSpan={4}>
                        <Text py={8} textAlign="center" color="gray.400" fontSize="sm">
                          <Trans i18nKey="customFields.emptyState" components={{ code: <Code /> }} />
                        </Text>
                      </Td>
                    </Tr>
                  )}
                  {data.fields.map((field) => (
                    <Tr key={field.id}>
                      <Td>
                        <Code>{`{{${field.key}}}`}</Code>
                        {field.fieldType === 'image' && (
                          <Badge ml={2} colorScheme="orange" fontSize="2xs">{t('customFields.badges.image')}</Badge>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={field.scope === 'room' ? 'purple' : 'teal'} fontSize="2xs">
                          {field.scope === 'room' ? t('customFields.badges.perRoom') : t('customFields.badges.hotelWide')}
                        </Badge>
                      </Td>
                      <Td fontSize="sm" maxW="380px" whiteSpace="normal">
                        {field.fieldType === 'image' ? (
                          field.scope === 'hotel' ? (
                            field.imageUrl ? (
                              <Image
                                src={absUrl(field.imageUrl)} alt={field.key}
                                maxH="32px" maxW="120px" objectFit="contain"
                              />
                            ) : (
                              <Text as="span" color="gray.400">{t('customFields.table.noImage')}</Text>
                            )
                          ) : (
                            t('customFields.table.roomsSet', { set: roomImageCount(field), count: data.rooms.length })
                          )
                        ) : field.scope === 'hotel' ? (
                          field.value || <Text as="span" color="gray.400">—</Text>
                        ) : (
                          t('customFields.table.roomsSet', { set: roomValueCount(field), count: data.rooms.length })
                        )}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton size="xs" icon={<EditIcon />} variant="ghost"
                            onClick={() => openEdit(field)} aria-label={t('common.edit')} />
                          <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                            onClick={() => { setDeleteTarget(field); deleteModal.onOpen(); }}
                            aria-label={t('common.delete')} />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </VStack>

      {/* Add / edit modal — for per-room keys this IS the values table */}
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size={form.scope === 'room' ? 'lg' : 'md'} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('customFields.modal.editTitle', { placeholder: `{{${form.key}}}` }) : t('customFields.buttons.addKey')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Shared hidden pickers — a per-room row remembers its target room */}
            <input
              type="file" accept={IMAGE_TYPES.join(',')} ref={hotelFileRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file) pickHotelImage(file);
              }}
            />
            <input
              type="file" accept={IMAGE_TYPES.join(',')} ref={roomFileRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file && roomPickTarget.current != null) pickRoomImage(roomPickTarget.current, file);
              }}
            />
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('customFields.modal.key')}</FormLabel>
                <Input
                  size="sm"
                  value={form.key}
                  placeholder={form.fieldType === 'image' ? 'logo' : 'door_code'}
                  onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                />
                <FormHelperText>
                  <Trans
                    i18nKey={form.fieldType === 'image' ? 'customFields.modal.keyHelpImage' : 'customFields.modal.keyHelp'}
                    values={{ tag: `{{${form.key || 'key'}}}`, urlTag: `{{${form.key || 'key'}_url}}` }}
                    components={{ code: <Code /> }}
                  />
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={!!editingId}>
                <FormLabel fontSize="sm">{t('customFields.modal.type')}</FormLabel>
                <RadioGroup
                  value={form.fieldType}
                  onChange={(fieldType) => setForm({ ...form, fieldType })}
                >
                  <HStack spacing={6}>
                    <Radio value="text" size="sm">{t('customFields.modal.typeText')}</Radio>
                    <Radio value="image" size="sm">{t('customFields.modal.typeImage')}</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              <FormControl isDisabled={!!editingId}>
                <FormLabel fontSize="sm">{t('customFields.table.scope')}</FormLabel>
                <RadioGroup
                  value={form.scope}
                  onChange={(scope) => setForm({ ...form, scope })}
                >
                  <HStack spacing={6}>
                    <Radio value="hotel" size="sm">{t('customFields.modal.scopeHotel')}</Radio>
                    <Radio value="room" size="sm">{t('customFields.modal.scopeRoom')}</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              {form.fieldType === 'image' && form.scope === 'hotel' && (
                <FormControl>
                  <FormLabel fontSize="sm">{t('customFields.badges.image')}</FormLabel>
                  {form.imagePreview || form.imageUrl ? (
                    <HStack spacing={3} align="center">
                      <Image
                        src={form.imagePreview || absUrl(form.imageUrl)} alt={t('customFields.modal.previewAlt')}
                        maxH="80px" maxW="200px" objectFit="contain"
                        borderWidth="1px" borderRadius="md" bg="gray.50" p={1}
                      />
                      <VStack align="start" spacing={1}>
                        <Button size="xs" onClick={() => hotelFileRef.current.click()}>{t('customFields.buttons.replace')}</Button>
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={removeHotelImage}>{t('customFields.buttons.remove')}</Button>
                      </VStack>
                    </HStack>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => hotelFileRef.current.click()}>
                      {t('customFields.buttons.uploadImage')}
                    </Button>
                  )}
                  <FormHelperText>{t('customFields.modal.imageHelp')}</FormHelperText>
                </FormControl>
              )}

              {form.fieldType === 'image' && form.scope === 'room' && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    {t('customFields.modal.imagePerRoom')}
                  </Text>
                  <TableContainer maxH="320px" overflowY="auto" borderWidth="1px" borderRadius="md">
                    <Table size="sm">
                      <Thead bg="gray.50" position="sticky" top={0}>
                        <Tr>
                          <Th w="90px">{t('customFields.table.room')}</Th>
                          <Th>{t('customFields.badges.image')}</Th>
                          <Th w="170px">{t('customFields.table.placeholder')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.rooms.map((room) => {
                          const id = String(room.id);
                          const preview = form.roomImagePreviews[id]
                            || (form.roomImages[id] ? absUrl(form.roomImages[id]) : '');
                          return (
                            <Tr key={room.id}>
                              <Td fontWeight="600" fontSize="sm">{room.number}</Td>
                              <Td>
                                {preview ? (
                                  <HStack spacing={1}>
                                    <Image
                                      src={preview} alt=""
                                      maxH="36px" maxW="90px" objectFit="contain"
                                      borderRadius="sm" bg="gray.50"
                                    />
                                    <IconButton size="xs" variant="ghost" icon={<EditIcon />}
                                      aria-label={t('customFields.buttons.replaceImage')} onClick={() => openRoomPicker(id)} />
                                    <IconButton size="xs" variant="ghost" colorScheme="red"
                                      icon={<CloseIcon boxSize={2} />} aria-label={t('customFields.buttons.removeImage')}
                                      onClick={() => removeRoomImage(id)} />
                                  </HStack>
                                ) : (
                                  <Button size="xs" variant="outline" onClick={() => openRoomPicker(id)}>
                                    {t('customFields.buttons.upload')}
                                  </Button>
                                )}
                              </Td>
                              <Td fontSize="xs" color="gray.500">
                                <Code fontSize="2xs">{`{{${form.key || 'key'}_${sanitize(room.number)}}}`}</Code>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    <Trans
                      i18nKey="customFields.modal.roomImageHelp"
                      values={{ tag: `{{${form.key || 'key'}}}` }}
                      components={{ code: <Code /> }}
                    />
                  </Text>
                </Box>
              )}

              {form.fieldType === 'text' && (form.scope === 'hotel' ? (
                <FormControl>
                  <FormLabel fontSize="sm">{t('customFields.table.value')}</FormLabel>
                  <Textarea
                    size="sm"
                    rows={3}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                  />
                </FormControl>
              ) : (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    {t('customFields.modal.valuePerRoom')}
                  </Text>
                  <TableContainer maxH="320px" overflowY="auto" borderWidth="1px" borderRadius="md">
                    <Table size="sm">
                      <Thead bg="gray.50" position="sticky" top={0}>
                        <Tr>
                          <Th w="110px">{t('customFields.table.room')}</Th>
                          <Th>{t('customFields.table.value')}</Th>
                          <Th w="170px">{t('customFields.table.placeholder')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.rooms.map((room) => (
                          <Tr key={room.id}>
                            <Td fontWeight="600" fontSize="sm">{room.number}</Td>
                            <Td>
                              <Input
                                size="xs"
                                value={form.roomValues[String(room.id)] || ''}
                                onChange={(e) => setForm({
                                  ...form,
                                  roomValues: { ...form.roomValues, [String(room.id)]: e.target.value },
                                })}
                              />
                            </Td>
                            <Td fontSize="xs" color="gray.500">
                              <Code fontSize="2xs">{`{{${form.key || 'key'}_${sanitize(room.number)}}}`}</Code>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    <Trans
                      i18nKey="customFields.modal.roomValueHelp"
                      values={{ tag: `{{${form.key || 'key'}}}` }}
                      components={{ code: <Code /> }}
                    />
                  </Text>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={modal.onClose}>{t('common.cancel')}</Button>
            <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editingId ? t('customFields.buttons.saveChanges') : t('customFields.buttons.createKey')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        onConfirm={handleDelete}
        title={t('customFields.deleteModal.title')}
        message={t('customFields.deleteModal.message', { placeholder: `{{${deleteTarget?.key}}}` })}
      />
    </PageShell>
  );
};

export default CustomFields;
